import { Preferences } from '@beyond-js/ui';
import { Observable } from '@cdn/state';

interface Entry {
	/** Query string of the entry URL; Landing may append `?lang=en|es` */
	search?: string;
	/**
	 * The tab's own storage, which keeps the entry URL's language for this tab: signing in through Beyond Accounts
	 * leaves the application and comes back to an address that no longer carries it
	 */
	tab?: Pick<Storage, 'getItem' | 'setItem'>;
	/** Languages the browser prefers, most preferred first */
	preferred?: readonly string[];
}

interface Specs extends Entry {
	/**
	 * The person's language and appearance (decision D07): the device copy on first paint, the account's values on
	 * arrival. Without it the language lasts for this page only.
	 */
	preferences?: Preferences;
}

/**
 * The selected interface language, held by the family's `Preferences`. The person's account decides it once the
 * application knows who they are; until then a language chosen on this device wins, then the entry URL's `lang`
 * (kept for the tab, so it survives the round trip through Beyond Accounts), then the browser preference, then
 * English. A choice made here applies on this device until the account's values change. `chain` is the explicit fallback
 * order of text lookups.
 */
export class Locale extends Observable {
	static readonly supported = ['en', 'es'] as const;
	static readonly fallback = 'en';
	static readonly #entry = 'beyond.cdn.locale.entry';

	#preferences: Preferences;
	#release: () => void;

	get code(): string {
		return this.#preferences.locale;
	}

	/**
	 * Lookup order: the selected language, then English. A key missing from Spanish shows its English text.
	 */
	get chain(): string[] {
		return this.code === Locale.fallback ? [Locale.fallback] : [this.code, Locale.fallback];
	}

	constructor({ preferences, ...entry }: Specs = {}) {
		super();
		this.#preferences = preferences ?? Locale.#own(entry);
		let code = this.code;
		this.#release = this.#preferences.subscribe(({ locale }) => {
			if (locale === code) return;
			code = locale;
			this.changed();
		});
	}

	/**
	 * The language before anything was chosen: the entry URL's, the tab's, the browser's, then English. It is the
	 * product default a device copy or the account overrides.
	 */
	static initial({ search = '', tab, preferred = [] }: Entry = {}): string {
		const requested = Locale.#carry(tab, Locale.match(new URLSearchParams(search).get('lang')));
		return [requested, ...preferred].map(candidate => Locale.match(candidate)).find(Boolean) ?? Locale.fallback;
	}

	/**
	 * The entry URL's language, kept for the tab when the address carries one, or the one the tab kept
	 */
	static #carry(tab: Entry['tab'], requested: string | undefined): string | null | undefined {
		try {
			if (requested) tab?.setItem(Locale.#entry, requested);
			return requested ?? tab?.getItem(Locale.#entry);
		} catch {
			// A blocked storage only loses the language across the sign-in
			return requested;
		}
	}

	// A language held for this page only, for a locale composed without the application's preferences
	static #own(entry: Entry): Preferences {
		const preferences = new Preferences({ key: 'beyond-cdn', fallback: { appearance: 'system', locale: Locale.initial(entry) }, storage: null, root: Locale.#detached() });
		preferences.restore();
		return preferences;
	}

	// An element outside the document, so a locale of its own never repaints the page
	static #detached(): Element | undefined {
		return typeof document === 'undefined' ? undefined : document.createElement('html');
	}

	/**
	 * @returns The supported language of a BCP 47 tag: `es-AR` selects `es`
	 */
	static match(tag: string | null | undefined): string | undefined {
		const language = String(tag ?? '').toLowerCase().split('-')[0];
		return (Locale.supported as readonly string[]).includes(language) ? language : undefined;
	}

	/**
	 * A language chosen in this application: this device only, until the account's value arrives again
	 */
	select(code: string) {
		const next = Locale.match(code);
		if (!next || next === this.code) return;
		this.#preferences.choose({ locale: next });
	}

	destroy() {
		this.#release();
	}
}
